import { useEffect, useRef, useState } from 'react';

import { JDSSearchBoxProps } from './generated';

export interface SearchBoxProps
  extends Omit<
    JDSSearchBoxProps,
    | 'onFocus'
    | 'onBlur'
    | 'onChange'
    | 'onSubmit'
    | 'onKeyup'
    | 'onKeydown'
    | 'onBack'
    | 'onSuffixClick'
  > {
  /**
   * When true, shows back button on focus.
   */
  showBack?: boolean;
  /**
   * Click event for the back button icon. Only fires when input has value.
   */
  onBack?: (value: string) => void;
  onPrefixClick?: (value?: string) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  onSuffixClick?: () => void;
  onFocus?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
  prefixAriaLabel?: string;
  suffixAriaLabel?: string;
}

export const withLogic =
  (Component: React.ComponentType<SearchBoxProps>): React.FC<SearchBoxProps> =>
  (props: SearchBoxProps) => {
    const { onBack, ...rest } = props;

    const [typing, setTyping] = useState(false);
    const [value, setValue] = useState(props.value);
    const element = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
      setValue(props.value);
    }, [props.value]);

    const triggerChange = (value = '') => {
      if (element) {
        const input = element?.current;
        const tracker = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        tracker?.call(input, value);
        const event = new Event('input', { bubbles: true });
        input?.dispatchEvent(event);
      }
    };
    const formatIconName = (iconName: string) => {
      if (typeof iconName === 'string' && iconName.startsWith('Ic')) {
        const words = iconName.slice(2).match(/[A-Z][a-z]+/g);
        return words ? words.join(' ').toLowerCase() + ' icon' : iconName;
      } else if (typeof iconName === 'string' && iconName.startsWith('ic_')) {
        const words = iconName.slice(3).split('_');
        return words ? words.join(' ').toLowerCase() + ' icon' : iconName;
      }
      return iconName;
    };
    return (
      <Component
        {...{
          ...rest,
          value,
          prefixAriaLabel:
            props.prefixAriaLabel || formatIconName(props.prefix as string),
          suffixAriaLabel:
            props.suffixAriaLabel || formatIconName(props.suffix as string),
          onBack: () => {
            if (focused && props.showBack) {
              onBack?.(value || '');
            } else {
              props.onPrefixClick?.(value);
            }
          },
          prefix: props.showBack && focused ? 'IcBack' : props.prefix,
          suffix: typing ? 'IcCloseRemove' : props.suffix,
          onFocus: (ev: React.FocusEvent<HTMLInputElement>) => {
            props.onFocus?.(ev);
            setFocused(true);
            setTyping(Boolean(ev.target.value));
          },
          _isValueEmpty: !typing,
          onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
            props.onBlur?.(ev);
            if (!ev.target.value) setFocused(false);
            if (!(ev.relatedTarget?.id === 'suffix-container'))
              setTyping(false);
          },
          onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            setTyping(Boolean((ev.target as HTMLInputElement).value));
            props.onChange?.(ev);
            setValue(ev.target.value);
          },
          layerRefs: { input: element },
          _inputFocus: focused,
          onSuffixClick: () => {
            props?.onSuffixClick?.();
            if (typing) {
              setValue('');
              triggerChange('');
              setTyping(false);
            }
          }
        }}
      />
    );
  };
